import { Link } from "@components/cms/link/Link";
import type { IModuleHero } from "@contentful-api/types/contentful";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import type { IModuleRenderedProps } from "@lib/contentful/modules-renderer/ModuleCreator";
import { Box } from "@ui/components/layout/box/Box";
import type { FlexProps } from "@ui/components/layout/flex/Flex";
import { Flex } from "@ui/components/layout/flex/Flex";
import { ECCOIcon } from "@ui/components/media-and-icons/ecco-icon/ECCOIcon";
import { NextImage } from "@ui/components/media-and-icons/image/NextImage";
import { useContrastText } from "@ui/hooks/useContrastText";
import { isEmpty } from "lodash-es";
import dynamic from "next/dynamic";
import type { FC } from "react";
import { useState } from "react";
import { renderRichTextToComponent } from "../rich-text-renderer/renderRichTextToComponent";
import { styles } from "../hero/HeroStyle";
import { MotionTitle } from "../hero/MotionTitle";
import { Button } from "@ui/components/forms/button/Button";

const headingDirectionMap: Record<
    IHeroProps["module"]["fields"]["headingPosition"],
    FlexProps["flexDirection"]
> = {
    left: ["column", "column", "column", "row-reverse"],
    right: ["column", "column", "column", "row"],
};

interface IHeroProps extends IModuleRenderedProps<IModuleHero> {
    slideIndex?: number; // Position of the Hero when used in HeroSlider
    hasNavigation?: boolean;
    height?: string | string[];
    color?: string;
    overlay: boolean;
}
const MotionDiv = dynamic(() => import("framer-motion").then((mod) => mod.motion.div), {
    ssr: false,
});

export const HeroPLP: FC<IHeroProps> = ({
    module,
    slideIndex = 0,
    // Per design we need this values, because we are placing the image behind, with absolute position to the rest of the elements but the Hero is relative
    height = ["200", "280", "280", "280"],
    hasNavigation,
    color = "black",
    overlay = true,
}) => {
    const {
        backgroundColor,
        backgroundVideo,
        headingPosition = "left",
        imageSize = "large",
        callToActions,
        description,
        image,
        imageMobile,
        title,
        legalText,
    } = module.fields;

    const id = module?.sys?.id;
    const isFullSizeImage = imageSize === "full width";
    const shouldRenderHeading = !isEmpty(title) || !isEmpty(description) || !isEmpty(callToActions);
    const shouldRenderImage = !isEmpty(image);
    const { isBlack } = useContrastText(backgroundColor?.value);

    const [animateCompleted, setAnimateCompleted] = useState(isEmpty(title) ?? false); // Initially set to true to start the animation

    const handleAnimationComplete = () => {
        setAnimateCompleted(true);
    };

    return (
        <Flex
            data-testid="Hero"
            w={"full"}
            mx="auto"
            minH={height}
            height={"fit-content"}
            position="relative"
            flexDirection={headingDirectionMap?.[headingPosition]}
            backgroundColor={backgroundColor}
        >
            {shouldRenderImage && (
                <>
                    <NextImage
                        loading={"eager"}
                        priority={true}
                        alt={image?.fields?.title}
                        src={image?.fields?.file?.url}
                        sx={{
                            height: "auto",
                            position: "absolute",
                            // This is the calc of the height of the image minus navigation and breadcrumbs container
                            top: ["-145px", "-160px"],
                            bottom: "0px",
                            ...(overlay && {
                                "&:after": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    width: "100%",
                                    height: "100%",
                                    background:
                                        "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.42) 50%, rgba(0, 0, 0, 0.48) 100%)",
                                },
                            }),
                        }}
                        display={{ base: !isEmpty(imageMobile) ? "none" : "block", md: "block" }}
                        sizes={["200vw, 200vw, 100vw, 100vw"]}
                        position={isFullSizeImage ? ["relative"] : null}
                        fill={true}
                        objectFit={"cover"}
                        top={0}
                        left={0}
                        zIndex={1}
                    />
                </>
            )}
            {shouldRenderHeading && (
                <Flex
                    style={{
                        top: "0",
                        flexDirection: "column",
                        justifyContent: "end",
                        width: "100%",
                    }}
                    zIndex={3}
                >
                    <Box
                        p={[3, 6]}
                        pb={6}
                        style={{
                            boxSizing: "border-box",
                            height: "100%",
                            display: "flex",
                        }}
                    >
                        <Flex
                            flexDirection="column"
                            w={["100%", "100%", "70%"]}
                            justifyContent="flex-end"
                            sx={{
                                ...(hasNavigation && {
                                    mb: {
                                        base: "14.4",
                                        sm: 0,
                                    },
                                }),
                                mt: "auto",
                            }}
                        >
                            {title && (
                                <MotionTitle
                                    sx={{ mb: [3, 4], color }}
                                    handleAnimationComplete={handleAnimationComplete}
                                    title={title}
                                    isFirstOnPage={true}
                                    data-contentful-entry-id={id}
                                    data-contentful-field-id={"title"}
                                />
                            )}
                            <MotionDiv
                                initial={{ opacity: 0 }}
                                animate={animateCompleted ? { opacity: 1 } : { opacity: 0 }}
                                transition={{ duration: 0.5 }}
                                style={{ display: "flex", flexDirection: "column" }}
                            >
                                {description && (
                                    <Box
                                        sx={{
                                            ...styles.description,
                                            color,
                                            order: 1,
                                            //@TODO: once the template is installed and handles only string/Long text entry type, remove this
                                            "& p": {
                                                fontSize: [
                                                    "mobileBodyTextLarge",
                                                    "tableHeading3",
                                                    "desktopBodyTextLarge",
                                                ],
                                                mb: 0,
                                                fontWeight: "bold",
                                            },
                                            mb: !legalText ? ["6", "8"] : [3, 2],
                                        }}
                                        color={color}
                                        data-contentful-entry-id={id}
                                        data-contentful-field-id={"description"}
                                    >
                                        {description}
                                    </Box>
                                )}
                                {legalText && (
                                    <Box
                                        sx={{
                                            ...styles.legalText,
                                            mb: [0, 0, 8],
                                            "& p": { mb: 0 },
                                        }}
                                        color={color}
                                        data-contentful-entry-id={id}
                                        order={{ base: 4, lg: 2 }}
                                        data-contentful-field-id={"legalText"}
                                    >
                                        {renderRichTextToComponent(legalText)}
                                    </Box>
                                )}
                                {callToActions && (
                                    <Flex
                                        width={[
                                            "calc(100vw - 30px)",
                                            "calc(100vw - 30px)",
                                            "calc(100vw - 75px)",
                                        ]}
                                        mb={[3, 3, 0]}
                                        order="3"
                                        gap={[0.5, 2]}
                                        wrap="wrap"
                                    >
                                        {callToActions?.map((button, i) => {
                                            const fields = button?.fields;
                                            return (
                                                <Button
                                                    sx={styles.button}
                                                    rightIcon={<ECCOIcon name="arrow-right" />}
                                                    as={Link}
                                                    key={`${button?.sys?.id} - ${i}`}
                                                    link={fields?.link}
                                                    variant={"standard"}
                                                    colorScheme={isBlack ? "black" : "white"}
                                                    data-contentful-entry-id={id}
                                                    data-contentful-field-id={"callToActions"}
                                                    onClick={() =>
                                                        AnalyticsClient.selectPromotion({
                                                            image: (image?.fields?.file?.url ??
                                                                backgroundVideo?.items?.[0]
                                                                    ?.thumbnail?.url) as string,
                                                            title: title + (fields?.label ?? ""),
                                                            index: slideIndex,
                                                            id,
                                                        })
                                                    }
                                                >
                                                    <span>{fields?.label}</span>
                                                </Button>
                                            );
                                        })}
                                    </Flex>
                                )}
                            </MotionDiv>
                        </Flex>
                    </Box>
                </Flex>
            )}
        </Flex>
    );
};
